import { Injectable } from '@angular/core';
import { InvoiceStore } from '../stores/invoice.store';
import { AnalyticsService } from './analytics.service';
import { RemoteLoggerService } from './remote-logger.service';
import { CheckoutStore } from '../stores/checkout.store';
import { AlertService } from './alert.service';
import { LocationStore } from '../stores/location.store';
import { FullScreenLoadingService } from './full-screen-loading.service';
import { NavController } from '@ionic/angular';
import { OrderPayStore } from '../stores/order-pay.store';
import {
  MonerisPaymentMethod,
  PaymentMethodOverrides,
  StandardPaymentMethod
} from '../pages/v2-order-pay/models/payment.model';
import { InvoiceService } from './invoice.service';
import { unawaited } from '../utility/unawaited';
import { BottomSheetService } from '../v2-components/services/bottom-sheet.service';
import { V2CurrencyPipe } from '../v2-components/pipes/v2-currency.pipe';
import { AsyncUtil } from 'utility/async.util';
import { hasKey } from 'utility/unknown.util';
import { Invoice2Store } from 'stores/invoice2.store';
import { RxUtil } from 'utility/rx.util';
import { TipStore } from 'stores/tip.store';
import { VerifyBillSelectionSheet } from '../pages/v2-order-pay/bottom-sheets/verify-bill-selection.sheet';
import { IPaymentResponse } from '../api/contracts/paymentresponse.contracts';
import { EmailSheetStore } from '../pages/v2-order-pay/components/v2-payment-options/common/email-sheet/email-sheet.store';

@Injectable({
  providedIn: 'root'
})
export class CheckoutService {
  private readonly defaultPaymentErrMsg =
    'Failed to submit payment for this invoice, please try again. If the issue persists, please pay your server.';
  private readonly invoiceExpiredErrMsg =
    'Someone has selected the same item as you! Please go back and update your selection';
  private readonly invoiceExpiredKey = 'INVOICE_EXPIRED_ERROR';
  private readonly paymentConfirmedTransitionDurationMs = 1500;

  private isSubmittingPayment = false;

  constructor(
    private invoiceStore: InvoiceStore,
    private analyticsService: AnalyticsService,
    private logger: RemoteLoggerService,
    private alertService: AlertService,
    private invoiceService: InvoiceService,
    private locationStore: LocationStore,
    private fullScreenLoadingService: FullScreenLoadingService,
    private navController: NavController,
    private orderPayStore: OrderPayStore,
    private bottomSheetService: BottomSheetService,
    private invoice2Store: Invoice2Store,
    private tipStore: TipStore
  ) { }

  async onPaymentError(error: unknown) {
    error = this.logger.normalizeError(error);

    let message = this.defaultPaymentErrMsg;
    if (hasKey(error, 'message') && typeof error.message === 'string') {
      if (error.message === this.invoiceExpiredKey) {
        message = this.invoiceExpiredErrMsg;
      } else {
        message = error.message;
      }
    }

    this.fullScreenLoadingService.hideFullScreenLoader();
    await this.alertService.showErrorAlert(message);
  }

  async confirmPayment(
    paymentCard: any,
    paymentMethod: StandardPaymentMethod | MonerisPaymentMethod,
    onPreSuccessHandlingHook?: () => Promise<void>,
    onPreErrorHandlingHook?: () => Promise<void>,
    providerOverride?: PaymentMethodOverrides,
    handleExceptions = true,
    extendTimer = true,
    recaptchaToken?: string
  ): Promise<IPaymentResponse> {
    // ensure only one payment can be submitted at a time
    if (this.isSubmittingPayment) return { success: false };
    this.isSubmittingPayment = true;

    try {
      this.analyticsService.log('Confirm Payment (button) Tapped');
      this.bottomSheetService.closeAllBottomSheets();
      this.fullScreenLoadingService.showFullScreenLoader();

      // If the current invoice is abandoned, try requesting a new invoice with same requst.
      if (this.invoiceStore.currentInvoice?.abandoned) {
        try {
          await this.invoiceService.requestInvoiceWithCachedInvoicePayloadTickets();
        } catch (e) {
          this.bottomSheetService.showBottomSheet(
            VerifyBillSelectionSheet,
            false
          );
          return { success: false };
        }
      } else if (extendTimer) {
        // Ensure the user still has a claim on the invoice
        await this.invoiceService.extendExpiryTimer();
      }

      const tipAmountDollars = await RxUtil.takeOne(
        this.tipStore.tipAmountDollars$
      );
      const paymentResponse = await this.invoiceService.submitPaymentForInvoice(
        (this.invoiceStore.currentInvoice?.id ||
          this.invoiceStore.currentInvoice?._id) ??
        '',
        tipAmountDollars,
        true,
        paymentCard,
        false,
        providerOverride,
        recaptchaToken
      );

      if (!paymentResponse.success) {
        this.fullScreenLoadingService.hideFullScreenLoader();
        this.isSubmittingPayment = false;
        return paymentResponse;
      }

      if (onPreSuccessHandlingHook) {
        await onPreSuccessHandlingHook();
      }

      const tipAmountPaid: number = paymentResponse.data.tipAmountPaid
        ? paymentResponse.data.tipAmountPaid
        : tipAmountDollars;
      const invoiceTotals = await RxUtil.takeOne(
        this.invoice2Store.invoiceTotals$
      );
      this.analyticsService.logTransaction(
        paymentResponse.data,
        this.locationStore.currentLocation?.name ?? '',
        invoiceTotals.subtotal / 100,
        tipAmountPaid / 100,
        invoiceTotals.tax / 100
      );
      this.analyticsService.setPaymentMethodUsed(paymentMethod);
      this.analyticsService.log('Confirm Payment Successful');

      try {
        this.fullScreenLoadingService.hideFullScreenLoader();
        await this.navController.navigateForward(
          [
            '/',
            this.orderPayStore.beaconId,
            'v2',
            'payment-confirmation',
            'confirmed'
          ],
          { replaceUrl: true }
        );
        await AsyncUtil.sleep(this.paymentConfirmedTransitionDurationMs);

        await this.navController.navigateRoot(
          ['/', this.orderPayStore.beaconId, 'v2', 'payment-confirmation'],
          { replaceUrl: true }
        );
      } catch (e) {
        this.logger.capture(e);
      }

      return paymentResponse;
    } catch (error) {
      if (!handleExceptions) throw error;

      this.analyticsService.log('Confirm Payment Failed');

      if (onPreErrorHandlingHook) {
        await onPreErrorHandlingHook();
      }

      unawaited(this.onPaymentError(error));
      return { success: false };
    } finally {
      this.fullScreenLoadingService.hideFullScreenLoader();
      this.isSubmittingPayment = false;
    }
  }
}
